import type { ComponentType } from 'react';
import { lazyImportForRecovery } from '../../lib/deployChunkRecovery';

export type RouteModuleLoader = () => Promise<{ default: ComponentType<any> }>;

type AuthenticatedRoute =
  | 'today'
  | 'practiceMobile'
  | 'practiceTablet'
  | 'practiceDesktop'
  | 'practiceReport'
  | 'cheatsheetVocabulary'
  | 'cheatsheetFlashcards'
  | 'cheatsheetPatterns'
  | 'cheatsheetMathFormulas';

type AuthenticatedRoutePreloadRegistry = {
  register: (route: AuthenticatedRoute, loader: RouteModuleLoader) => RouteModuleLoader;
  getLoader: (route: AuthenticatedRoute) => RouteModuleLoader;
  preload: (pathname: string) => Promise<{ default: ComponentType<any> }> | null;
};

export type RoutePreloader = (
  pathname: string,
) => Promise<{ default: ComponentType<any> }> | null;

export type AuthenticatedRouteHandoff = {
  routePromise: Promise<{ default: ComponentType<any> }>;
  nextAppPromise: Promise<unknown>;
};

export function createAuthenticatedRoutePreloadRegistry(): AuthenticatedRoutePreloadRegistry {
  const loaders: Partial<Record<AuthenticatedRoute, RouteModuleLoader>> = {};
  const promises: Partial<Record<AuthenticatedRoute, Promise<{ default: ComponentType<any> }>>> = {};

  const register = (route: AuthenticatedRoute, loader: RouteModuleLoader): RouteModuleLoader => {
    const cachedLoader: RouteModuleLoader = () => {
      return promises[route] ??= loader();
    };
    loaders[route] = cachedLoader;
    return cachedLoader;
  };

  const getLoader = (route: AuthenticatedRoute) => {
    const loader = loaders[route];
    if (!loader) throw new Error(`Authenticated route loader is not registered: ${route}`);
    return loader;
  };

  const preload = (pathname: string) => {
    const path = pathname.replace(/\/$/, '');
    const reportId = path.startsWith('/insights/activity/practice/')
      ? path.slice('/insights/activity/practice/'.length)
      : '';
    const route = path === '/today' ? 'today'
      : path === '/practice' ? 'practiceMobile'
        : reportId && !reportId.includes('/') ? 'practiceReport'
          : path === '/cheatsheet/vocabulary' ? 'cheatsheetVocabulary'
            : path === '/cheatsheet/flashcards' ? 'cheatsheetFlashcards'
              : path === '/cheatsheet/patterns' ? 'cheatsheetPatterns'
                : path === '/cheatsheet/math-formulas' ? 'cheatsheetMathFormulas'
                  : null;
    const loader = route ? loaders[route] : undefined;
    return loader ? loader() : null;
  };

  return { register, getLoader, preload };
}

type PracticeLayout = 'PracticeV2Mobile' | 'PracticeV2Tablet' | 'PracticeV2Desktop';
type PracticeLayoutsModule = Record<PracticeLayout, ComponentType<any>>;

const registry = createAuthenticatedRoutePreloadRegistry();

registry.register(
  'practiceReport',
  lazyImportForRecovery(() => import('./review/PracticeReport'), 'NextApp/review/PracticeReport'),
);
registry.register(
  'cheatsheetPatterns',
  lazyImportForRecovery(
    () => import('./insights/cheatsheet/PatternsPage').then(({ PatternsPage: component }) => ({ default: component })),
    'NextApp/insights/cheatsheet/PatternsPage',
  ),
);
registry.register(
  'cheatsheetVocabulary',
  lazyImportForRecovery(
    () => import('./insights/cheatsheet/vocab/VocabPage').then(({ VocabPage: component }) => ({ default: component })),
    'NextApp/insights/cheatsheet/vocab/VocabPage',
  ),
);
registry.register(
  'cheatsheetFlashcards',
  lazyImportForRecovery(
    () => import('./insights/cheatsheet/vocab/VocabFlashcardDrill').then(({ VocabFlashcardDrill: component }) => ({ default: component })),
    'NextApp/insights/cheatsheet/vocab/VocabFlashcardDrill',
  ),
);
registry.register(
  'cheatsheetMathFormulas',
  lazyImportForRecovery(
    () => import('./insights/cheatsheet/MathFormulasPage').then(({ MathFormulasPage: component }) => ({ default: component })),
    'NextApp/insights/cheatsheet/MathFormulasPage',
  ),
);

let practiceLayoutsPromise: Promise<PracticeLayoutsModule> | undefined;
const loadPracticeLayouts = () => practiceLayoutsPromise ??= import('./practice-v2/layouts');

function registerPracticeLayout(
  route: 'practiceMobile' | 'practiceTablet' | 'practiceDesktop',
  component: PracticeLayout,
): void {
  registry.register(
    route,
    lazyImportForRecovery(
      () => loadPracticeLayouts().then((module) => ({ default: module[component] })),
      `NextApp/practice-v2/layouts#${component}`,
    ),
  );
}

registerPracticeLayout('practiceMobile', 'PracticeV2Mobile');
registerPracticeLayout('practiceTablet', 'PracticeV2Tablet');
registerPracticeLayout('practiceDesktop', 'PracticeV2Desktop');

registry.register(
  'today',
  lazyImportForRecovery(
    () => import('./screens').then(({ HomeV2: component }) => ({ default: component })),
    'NextApp/screens/HomeV2',
  ),
);

export const authenticatedRoutePreloadRegistry = registry;
export const preloadAuthenticatedRoute = authenticatedRoutePreloadRegistry.preload;

export function startAuthenticatedRouteHandoff(
  pathname: string,
  nextAppLoader: () => Promise<unknown>,
  routePreloader: RoutePreloader = preloadAuthenticatedRoute,
): AuthenticatedRouteHandoff | null {
  const routePromise = routePreloader(pathname);
  if (!routePromise) return null;

  return {
    routePromise,
    nextAppPromise: nextAppLoader(),
  };
}
